import { ContactPersonInfo } from "@/data/models/ProjectDetails";

/**
 * What an apartment label says about its flat, as IRES Project's `formatApartmentHeader`:
 * "H. Hubschneider (Keller)", else the name alone, else the Adresszusatz alone. The first name only
 * contributes its initial, also without a last name ("H. (Keller)").
 *
 * @param contactPerson the flat's contact person
 * @param showFloor `false` drops the Adresszusatz next to a name; a flat without a name still shows
 * it, or the label would say nothing about the flat
 * @returns the details, or `""` when there are none
 */
export function formatApartmentDetails(
  contactPerson: Pick<ContactPersonInfo, "firstName" | "lastName" | "floor">,
  showFloor = true
): string {
  const initial = contactPerson.firstName?.trim().charAt(0);
  const name = [initial ? `${initial}.` : "", contactPerson.lastName?.trim() ?? ""]
    .filter((part) => part !== "")
    .join(" ");
  const floor = contactPerson.floor?.trim() ?? "";

  if (name !== "" && floor !== "") {
    return showFloor ? `${name} (${floor})` : name;
  }

  return name || floor;
}

/**
 * Joins the apartment label and its details: "Wohnung 2: H. Hubschneider (Keller)" when the label
 * carries a number, "Wohnung H. Hubschneider (Keller)" when it does not (a single flat), since
 * there is no number for the colon to attach to.
 *
 * @param label "Wohnung 2", or "Wohnung" for a single flat
 * @param details see {@link formatApartmentDetails}
 * @param labelHasNumber whether `label` shows the apartment number
 */
export function joinApartmentLabel(label: string, details: string, labelHasNumber: boolean): string {
  if (details === "") {
    return label;
  }

  return labelHasNumber ? `${label}: ${details}` : `${label} ${details}`;
}
